import React, {useEffect, useState} from 'react';
import {Autocomplete, CircularProgress, InputLabel, Stack, TextField} from '@mui/material';
import {debounceTime, distinctUntilChanged, Observable, switchMap, tap} from 'rxjs';
import {createEffectFn} from '@ngneat/effects';
import {useEffectFn} from '@ngneat/effects-hooks';

import {SelectItem} from '@components/inputs/select/Select.component';

import {ArrowDown} from '@icons/index';

interface SearchParams {
  getOptions: (search?: string) => Observable<SelectItem[]>;
  search?: string;
  setLoading: (loading: boolean) => void;
  setOptions: (options: any[]) => void;
}

export const searchEffect = createEffectFn((search$: Observable<SearchParams>) => {
  return search$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(({setLoading}) => setLoading(true)),
    switchMap(({getOptions, search, setLoading, setOptions}) =>
      getOptions(search).pipe(
        tap((options) => {
          setOptions(options);
          setLoading(false);
        }),
      )
    ),
  );
});

interface AsyncSelectWithSearchComponentProps {
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  error?: string;
  getOptions: (search?: string) => Observable<SelectItem[]>;
  handleChange?: (value: SelectItem) => void;
  label?: string;
  labelWidth?: number;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  value?: SelectItem | '';
}

const AsyncSelectWithSearchComponent = (props: AsyncSelectWithSearchComponentProps) => {
  const {
    color = 'primary',
    disabled,
    error,
    getOptions,
    handleChange,
    label,
    labelWidth,
    placeholder,
    readOnly,
    required,
    value = '',
  } = props;

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly any[]>([]);

  const [loading, setLoading] = useState(false);

  const searchOptions = useEffectFn(searchEffect);

  useEffect(() => {
    if (open) {
      setLoading(true);
      searchOptions({getOptions, setLoading, setOptions});
    } else if (!open) {
      setOptions([]);
    }
  }, [getOptions, searchOptions, open]);

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
      {!!label && (
        <InputLabel error={!!error && !disabled} sx={{width: labelWidth ? `${labelWidth}px` : undefined}}>
          {label + (required ? '*' : '')}
        </InputLabel>
      )}
      <Autocomplete
        open={open}
        onOpen={disabled || readOnly ? undefined : () => setOpen(true)}
        onClose={disabled || readOnly ? undefined : () => setOpen(false)}
        getOptionLabel={(option: SelectItem) => option.label || ''}
        disabled={disabled}
        isOptionEqualToValue={(option: SelectItem, value: any) => typeof value !== 'string' && !!value && option.value === value.value}
        options={options}
        loading={loading}
        value={value}
        sx={{
          '& .MuiAutocomplete-input': {
            padding : '9px 32px 14px 4px !important'
          }
        }}
        readOnly={readOnly}
        filterOptions={(options) => options}
        onChange={(evt, newValue) => {
          handleChange?.(newValue);
        }}
        renderOption={(props, option: SelectItem) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        popupIcon={!readOnly ? <ArrowDown/> : null}
        onInputChange={(evt, inputValue) => {
          if (evt?.type !== 'click') {
            searchOptions({search: inputValue, getOptions, setLoading, setOptions});
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            color={color}
            focused={readOnly ? false : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20}/> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              placeholder: !value && !readOnly ? placeholder : undefined,
            }}
            error={!!error && !disabled}
            helperText={!disabled ? error : undefined}
          />
        )}/>
    </Stack>
  );
};

export default AsyncSelectWithSearchComponent;
